import { useMemo } from 'react';
import _ from 'lodash';
import { Modal } from './design-system/Modal';
import { formatRewardsType } from '../data/shared/utils/formatRewardsType';
import { EditableTableCell } from './design-system/EditableTableCell';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CreditCardRewardsType } from 'data/shared/CreditCardRewardsTypes';
import { CreditCard } from 'data/shared/CreditCard';
import { CreditCardDataFiltered } from 'data/shared/CreditCardData';
import {
  promotionalTypesConfig,
  getApplicablePromotionalTypes,
  groupPromotionalTypesByFamily,
} from 'data/shared/promotionalTypes';
import { PromotionalType } from 'data/shared/__autogenerated__/types/PromotionalType';
import { useURLParamPromotionalTypes } from '../hooks/useURLParamPromotionalTypes';

export const EditPointValues = ({
  handleClose,
  cards,
  pointValues,
  resetPointValues,
  setPointValues,
  promotionalTypes: promotionalTypesProp,
  setPromotionalTypes: setPromotionalTypesProp,
  resetPromotionalTypes: resetPromotionalTypesProp,
}: {
  cards: CreditCard[];
  pointValues: Record<CreditCardRewardsType, number>;
  setPointValues: (rewardsType: CreditCardRewardsType, value: string) => void;
  resetPointValues: (_?: CreditCardRewardsType) => void;
  handleClose: () => void;
  promotionalTypes?: PromotionalType[];
  setPromotionalTypes?: (types: PromotionalType[]) => void;
  resetPromotionalTypes?: () => void;
}) => {
  // Use provided promotional types state, or fall back to hook if not provided
  const hookState = useURLParamPromotionalTypes(cards);
  const promotionalTypes = promotionalTypesProp ?? hookState.promotionalTypes;
  const setPromotionalTypes =
    setPromotionalTypesProp ?? hookState.setPromotionalTypes;
  const resetPromotionalTypes =
    resetPromotionalTypesProp ?? hookState.resetPromotionalTypes;
  const pointValuesToShow: CreditCardRewardsType[] = useMemo(
    () =>
      _.uniq(
        cards
          .map((card) => CreditCardDataFiltered[card]?.rewardsType)
          .filter(Boolean)
      )
        .filter(
          (rewardsType) => rewardsType !== CreditCardRewardsType.CASH_BACK
        )
        .sort(),
    [cards]
  );

  // Get applicable promotional types for the selected cards
  const applicablePromotionalTypes = useMemo(
    () => getApplicablePromotionalTypes(cards),
    [cards]
  );

  return (
    <Modal
      isOpen={true}
      handleClose={handleClose}
      style={{ maxWidth: '600px' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <h2>Advanced Settings</h2>
      </div>

      {/* Point Values Section */}
      <section
        style={{
          marginBottom: '32px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#000',
            }}
          >
            Point Values
          </h3>
          <button
            onClick={() => resetPointValues()}
            style={{
              background: 'none',
              border: 'none',
              color: '#1976d2',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <FontAwesomeIcon icon={faRefresh} /> Reset
          </button>
        </div>
        <p
          style={{
            marginTop: 0,
            marginBottom: '16px',
            fontSize: '14px',
            color: '#666',
          }}
        >
          Only showing point currencies for cards that you have selected.
        </p>
        {pointValuesToShow.map((rewardsType) => (
          <div
            key={rewardsType}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <label
              style={{
                marginRight: 5,
                fontFamily: 'Merriweather',
                color: '#000',
              }}
            >
              {formatRewardsType(rewardsType)}
            </label>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {rewardsType === CreditCardRewardsType.CASH_BACK ? (
                <strong>1</strong>
              ) : (
                <EditableTableCell
                  value={String(pointValues[rewardsType])}
                  onChange={(value) => setPointValues(rewardsType, value)}
                />
              )}
              <span style={{ marginLeft: 5 }}>cent(s) per point</span>
            </div>
          </div>
        ))}
      </section>

      {/* Promotional Types Section */}
      {applicablePromotionalTypes.length > 0 && (
        <section>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#000',
              }}
            >
              Promotional Types
            </h3>
            <button
              onClick={() => resetPromotionalTypes()}
              style={{
                background: 'none',
                border: 'none',
                color: '#1976d2',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <FontAwesomeIcon icon={faRefresh} /> Reset
            </button>
          </div>
          <p
            style={{
              marginTop: 0,
              marginBottom: '16px',
              fontSize: '14px',
              color: '#666',
            }}
          >
            Configure your promotional statuses to see accurate earn rates.
          </p>
          {Array.from(
            groupPromotionalTypesByFamily(applicablePromotionalTypes).entries()
          ).map(([familyKey, familyTypes]: [string, PromotionalType[]]) => {
            // If there's only one type in the family, render as boolean checkbox
            if (familyTypes.length === 1) {
              const promoType = familyTypes[0];
              const config = promotionalTypesConfig[promoType];
              if (!config || config.type === 'enum') {
                // Enum types are not supported with the new array format
                // These should be converted to tier-specific types
                // For now, skip rendering enum types
                return null;
              }

              // Boolean type
              const isSelected = promotionalTypes.includes(promoType);
              return (
                <div
                  key={promoType}
                  style={{
                    marginBottom: '16px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                  }}
                >
                  <input
                    type="checkbox"
                    id={promoType}
                    checked={isSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Add the type if not already present
                        if (!promotionalTypes.includes(promoType)) {
                          setPromotionalTypes([...promotionalTypes, promoType]);
                        }
                      } else {
                        // Remove the type
                        setPromotionalTypes(
                          promotionalTypes.filter((t) => t !== promoType)
                        );
                      }
                    }}
                    style={{
                      marginRight: '12px',
                      marginTop: '4px',
                      cursor: 'pointer',
                    }}
                  />
                  <label
                    htmlFor={promoType}
                    style={{
                      flex: 1,
                      cursor: 'pointer',
                      fontFamily: 'Merriweather',
                      color: '#000',
                    }}
                  >
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                      {config.label}
                    </div>
                    {config.description && (
                      <div
                        style={{
                          fontSize: '13px',
                          color: '#666',
                          fontWeight: 'normal',
                        }}
                      >
                        {config.description}
                      </div>
                    )}
                  </label>
                </div>
              );
            } else {
              // Multiple types in family - render as radio buttons
              const hasSelectedType = familyTypes.some((type) =>
                promotionalTypes.includes(type)
              );
              return (
                <div
                  key={familyKey}
                  style={{
                    marginBottom: '24px',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      fontFamily: 'Merriweather',
                      color: '#000',
                      fontWeight: '500',
                      marginBottom: '8px',
                    }}
                  >
                    {familyKey === 'bank_of_america_preferred_rewards'
                      ? 'Bank of America Preferred Rewards'
                      : familyKey === 'us_bank_smartly'
                      ? 'U.S. Bank Smartly Tier'
                      : familyKey === 'bilt_spend_ratio'
                      ? 'Everyday spend ratio (% of housing)'
                      : 'Promotional Type'}
                  </label>
                  {familyKey === 'bank_of_america_preferred_rewards' && (
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#b45309',
                        backgroundColor: '#fef3c7',
                        border: '1px solid #fde68a',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        margin: '0 0 12px 0',
                      }}
                    >
                      These tiers will be replaced by BofA Rewards on May 27,
                      2026 with higher balance requirements.
                    </p>
                  )}
                  {familyKey === 'bilt_spend_ratio' && (
                    <p
                      style={{
                        fontSize: '13px',
                        color: '#666',
                        margin: '0 0 12px 0',
                      }}
                    >
                      Your housing points depend on how much you spend on
                      everyday purchases relative to your monthly housing payment.
                    </p>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    {/* None option */}
                    <label
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name={familyKey}
                        checked={!hasSelectedType}
                        onChange={() => {
                          // Remove all types in this family
                          const newTypes = promotionalTypes.filter(
                            (t) => !familyTypes.includes(t)
                          );
                          setPromotionalTypes(newTypes);
                        }}
                        style={{
                          marginRight: '10px',
                          marginTop: '4px',
                          cursor: 'pointer',
                        }}
                      />
                      <div>
                        <div
                          style={{
                            fontFamily: 'Merriweather',
                            color: '#000',
                            fontSize: '14px',
                          }}
                        >
                          None
                        </div>
                      </div>
                    </label>
                    {/* Tier options */}
                    {familyTypes.map((promoType: PromotionalType) => {
                      const config = promotionalTypesConfig[promoType];
                      if (!config || config.type === 'enum') {
                        return null;
                      }
                      const isSelected = promotionalTypes.includes(promoType);
                      return (
                        <label
                          key={promoType}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="radio"
                            name={familyKey}
                            checked={isSelected}
                            onChange={() => {
                              // For radio groups, selecting one clears others in the family
                              // First, remove all types in this family
                              const newTypes = promotionalTypes.filter(
                                (t) => !familyTypes.includes(t)
                              );
                              // Then add the selected type
                              setPromotionalTypes([...newTypes, promoType]);
                            }}
                            style={{
                              marginRight: '10px',
                              marginTop: '4px',
                              cursor: 'pointer',
                            }}
                          />
                          <div>
                            <div
                              style={{
                                fontFamily: 'Merriweather',
                                color: '#000',
                                fontSize: '14px',
                              }}
                            >
                              {config.label}
                            </div>
                            {config.description && (
                              <div
                                style={{
                                  fontSize: '12px',
                                  color: '#666',
                                  marginTop: '2px',
                                }}
                              >
                                {config.description}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            }
          })}
        </section>
      )}
    </Modal>
  );
};
